<template>
  <div 
    :class="[
      'flex items-center justify-center',
      'bg-gradient-to-br',
      gradientClass,
      className
    ]"
    :style="containerStyle"
  >
    <div class="text-center">
      <!-- 图标 -->
      <component 
        :is="iconComponent" 
        :class="[
          'mx-auto mb-2',
          iconSizeClass,
          iconColorClass
        ]"
        v-if="showIcon"
      />
      
      <!-- 文字 -->
      <div 
        v-if="text"
        :class="[
          'font-medium',
          textSizeClass,
          textColorClass
        ]"
      >
        {{ text }}
      </div>
      
      <!-- 尺寸信息 -->
      <div 
        v-if="showDimensions && (width || height)"
        :class="[
          'text-xs mt-1',
          textColorClass
        ]"
      >
        {{ dimensions }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  PhotoIcon,
  VideoCameraIcon,
  DocumentIcon,
  BuildingOfficeIcon,
  CogIcon,
  WrenchScrewdriverIcon,
  Squares2X2Icon
} from '@heroicons/vue/24/outline'

interface Props {
  width?: number | string
  height?: number | string
  type?: 'image' | 'video' | 'document' | 'building' | 'gear' | 'wrench' | 'grid'
  text?: string
  className?: string
  showIcon?: boolean
  showDimensions?: boolean
  gradient?: 'blue' | 'green' | 'purple' | 'gray' | 'orange'
}

const props = withDefaults(defineProps<Props>(), {
  type: 'image',
  showIcon: true,
  showDimensions: false,
  gradient: 'gray'
})

// 图标组件映射
const iconMap = {
  image: PhotoIcon,
  video: VideoCameraIcon,
  document: DocumentIcon,
  building: BuildingOfficeIcon,
  gear: CogIcon,
  wrench: WrenchScrewdriverIcon,
  grid: Squares2X2Icon
}

// 渐变样式映射
const gradientMap = {
  blue: 'from-blue-400 to-blue-600',
  green: 'from-green-400 to-green-600',
  purple: 'from-purple-400 to-purple-600',
  gray: 'from-gray-300 to-gray-400',
  orange: 'from-orange-400 to-orange-600'
}

// 计算属性
const iconComponent = computed(() => iconMap[props.type])

const gradientClass = computed(() => gradientMap[props.gradient])

const containerStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.width) {
    style.width = typeof props.width === 'number' ? `${props.width}px` : props.width
  }
  if (props.height) {
    style.height = typeof props.height === 'number' ? `${props.height}px` : props.height
  }
  return style
})

// 根据容器尺寸自动调整图标大小
const iconSizeClass = computed(() => {
  const height = typeof props.height === 'number' ? props.height : parseInt(props.height as string) || 200
  if (height < 60) return 'w-4 h-4'
  if (height < 80) return 'w-6 h-6'
  if (height < 120) return 'w-8 h-8'
  if (height < 200) return 'w-12 h-12'
  return 'w-16 h-16'
})

const textSizeClass = computed(() => {
  const height = typeof props.height === 'number' ? props.height : parseInt(props.height as string) || 200
  if (height < 100) return 'text-xs'
  if (height < 200) return 'text-sm'
  return 'text-base'
})

const iconColorClass = computed(() => {
  return props.gradient === 'gray' ? 'text-white' : 'text-white/80'
})

const textColorClass = computed(() => {
  return props.gradient === 'gray' ? 'text-gray-600' : 'text-white/90'
})

const dimensions = computed(() => {
  const w = props.width ? (typeof props.width === 'number' ? props.width : parseInt(props.width as string)) : 0
  const h = props.height ? (typeof props.height === 'number' ? props.height : parseInt(props.height as string)) : 0
  
  if (w && h) return `${w}×${h}`
  if (w) return `${w}宽`
  if (h) return `${h}高`
  return ''
})
</script>

<style scoped>
/* 确保容器有最小高度，避免塌陷 */
div[class*="bg-gradient-to-br"] {
  min-height: 40px;
}
</style>